import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, type ApiError, type OpenApprovalFromEmailResult } from '@whinerp/client';
import { Button } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { ApprovalChainView } from '../approvals/approval-chain.js';
import { defineScreen } from '../types.js';

/**
 * SC-69 Approve from an email (F-090).
 *
 * The link in an approver's email opens this: the whole chain, and approve or
 * reject as the person the link was sent to. A rejection still needs its
 * reason - an email is not an exemption from saying why.
 */
function ApproveFromEmailScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const token = useParams()['token'] ?? '';
  const [comment, setComment] = useState('');
  const opened = useQuery<OpenApprovalFromEmailResult, ApiError>({
    queryKey: ['approval-from-email', token],
    queryFn: () => api.openApprovalFromEmail({ body: { token } }),
    retry: false,
  });
  const decide = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => api.decideApprovalFromEmail({ body: { token, decision, comment: comment.trim() || null } }),
  });

  /*
   * The component names itself in every state.
   *
   * An approval link arrives by email and is opened by somebody who is often
   * not signed in: loading and "this link is no longer valid" are the states
   * they are most likely to see, and a screen that only identifies itself
   * once it has data cannot be found in either.
   */
  if (opened.isPending) {
    return (
      <main data-component="ApproveFromEmail" className="mx-auto flex min-h-screen w-full max-w-reading flex-col gap-2 px-gutter py-6">
        <p role="status" className="text-body text-ink-meta">{t('state.loading')}</p>
      </main>
    );
  }
  const chain = decide.data?.request ?? opened.data?.request ?? null;
  if (opened.isError || !chain) {
    return (
      <main data-component="ApproveFromEmail" className="mx-auto flex min-h-screen w-full max-w-reading flex-col gap-2 px-gutter py-6">
        <h1 data-approval-link-invalid className="text-h2 text-ink">{t('approvals.linkInvalid')}</h1>
      </main>
    );
  }
  return (
    <main data-component="ApproveFromEmail" className="mx-auto flex min-h-screen w-full max-w-reading flex-col gap-4 px-gutter py-6">
      <h1 className="text-h2 text-ink">{chain.recordName}</h1>
      <p className="text-meta text-ink-meta">
        {chain.processName} · {t('approvals.askedBy', { values: { name: chain.requestedByName ?? '' } })}
      </p>
      <ApprovalChainView chain={chain} />
      {decide.error ? <p data-approval-refusal className="text-dense text-danger-text">{errorText(decide.error)}</p> : null}
      {chain.status === 'pending' && !decide.data ? (
        <div className="flex flex-col gap-2">
          <textarea
            data-email-comment
            rows={3}
            aria-label={t('approvals.comment')}
            placeholder={t('approvals.comment')}
            className="rounded-control border border-control bg-surface px-3 py-2 text-body text-ink"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button intent="primary" data-email-approve disabled={decide.isPending} onClick={() => decide.mutate('approved')}>
              {t('approvals.approve')}
            </Button>
            <Button intent="secondary" data-email-reject disabled={decide.isPending} onClick={() => decide.mutate('rejected')}>
              {t('approvals.reject')}
            </Button>
          </div>
        </div>
      ) : decide.data ? (
        <p data-email-decided className="text-body text-ink">{t('approvals.recorded')}</p>
      ) : null}
    </main>
  );
}

function Loading(): JSX.Element {
  const t = useT();
  return <p role="status" className="p-6 text-body text-ink-meta">{t('state.loading')}</p>;
}
function Invalid(): JSX.Element {
  const t = useT();
  return <p className="p-6 text-body text-ink-meta">{t('approvals.linkInvalid')}</p>;
}

export const screen = defineScreen({
  id: 'SC-69',
  route: '/approve/:token',
  surface: 'page',
  chrome: 'bare',
  title: 'Approve from email',
  features: ['F-090'],
  entitlement: 'always-on',
  reachedBy: [{ kind: 'public' }],
  sampleParams: { token: 'sample-not-a-token' },
  components: [],
  elements: ['ApproveFromEmail'],
  states: { empty: Invalid, loading: Loading, error: Invalid },
  component: ApproveFromEmailScreen,
});

export default screen;
